import axios from 'axios'
import countries from 'country-list'
import {Raven} from 'vue-raven'
import {fetchAccountStats} from '@/api/stats'
import {tagsApi} from '@/api/tags'
import {
    fetchCurrencies,
    fetchLocations,
    fetchPaymentTerms,
    fetchSubsidiaries,
    fetchTaxOptions,
    fetchTaxTreatments,
    fetchUnitOfMeasure
} from '@/api/data'
import {isPlainObject} from 'lodash'

export const DATA_SET_INTEGRATION_UUID = 'DATA_SET_INTEGRATION_UUID'
export const DATA_SET_TAX_RATES = 'DATA_SET_TAX_RATES'
export const DATA_SET_TAX_TREATMENT = 'DATA_SET_TAX_TREATMENT'
export const DATA_SET_UNIT_OF_MEASURE = 'DATA_SET_UNIT_OF_MEASURE'
export const DATA_SET_INTEGRATIONS = 'DATA_SET_INTEGRATIONS'
export const DATA_SET_TEAM_INTEGRATIONS = 'DATA_SET_TEAM_INTEGRATIONS'
export const DATA_SET_RELATED_TEAMS = 'DATA_SET_RELATED_TEAMS'
export const DATA_SET_LOCATIONS = 'DATA_SET_LOCATIONS'
export const DATA_SET_SUBSIDIARIES = 'DATA_SET_SUBSIDIARIES'
export const DATA_SET_CURRENCIES = 'DATA_SET_CURRENCIES'
export const DATA_SET_PAYMENT_TERMS = 'DATA_SET_PAYMENT_TERMS'
export const DATA_SET_DOCUMENT_TAGS = 'DATA_SET_DOCUMENT_TAGS'
export const DATA_SET_ACCOUNT_STATS = 'DATA_SET_ACCOUNT_STATS'

export const DATA_FETCH_LOCATIONS = 'DATA_FETCH_LOCATIONS'
export const DATA_FETCH_SUBSIDIARIES = 'DATA_FETCH_SUBSIDIARIES'
export const DATA_FETCH_CURRENCIES = 'DATA_FETCH_CURRENCIES'
export const DATA_FETCH_ENTITIES = 'DATA_FETCH_ENTITIES'
export const DATA_FETCH_TEAM_DEPENDENT = 'DATA_FETCH_TEAM_DEPENDENT'
export const DATA_FETCH_TAX_RATES = 'DATA_FETCH_TAX_RATES'
export const DATA_FETCH_TAX_TREATMENTS = 'DATA_FETCH_TAX_TREATMENTS'
export const DATA_FETCH_UNIT_OF_MEASURE = 'DATA_FETCH_UNIT_OF_MEASURE'
export const DATA_FETCH_INTEGRATIONS = 'DATA_FETCH_INTEGRATIONS'
export const DATA_FETCH_TEAM_INTEGRATIONS = 'DATA_FETCH_TEAM_INTEGRATIONS'
export const DATA_FETCH_PAYMENT_TERMS = 'DATA_FETCH_PAYMENT_TERMS'
export const DATA_FETCH_DOCUMENT_TAGS = 'DATA_FETCH_DOCUMENT_TAGS'
export const DATA_FETCH_ACCOUNT_STATS = 'DATA_FETCH_ACCOUNT_STATS'

const state = {
    relatedTeams: [],
    accountStats: {}, // AccountStats
    documentTags: [],
    selectedIntegrationUuid: null,
    // 1-20 records
    taxRateList: [],
    taxTreatmentList: [],
    integrationsList: [],
    teamIntegrationsList: [],

    // 1-500 records
    locations: [],
    subsidiaries: [],
    currencyList: [],
    unitOfMeasureList: []
}

const getters = {
    relatedTeams: state => state.relatedTeams,
    accountStats: state => state.accountStats,
    documentTags: state => state.documentTags.filter(tag => isPlainObject(tag) && tag.uuid),
    getSingleDocumentTagOptions: (state, getters) => doc => {
        if (doc && _.isArray(doc.tags)) {
            return doc.tags.map(tagId => getters.documentTags.find(tag => tag.uuid === tagId))
        }
        return []
    },
    unitOfMeasureList: state => state.unitOfMeasureList,
    countryList: () => countries.getData(),
    currencyList: state => state.currencyList,
    taxRateList: state => state.taxRateList,
    taxTreatmentList: state => state.taxTreatmentList,
    paymentTermsList: state => state.paymentTermsList,

    // integrations
    integrationsList: state => state.integrationsList,
    teamIntegrationsList: state => state.teamIntegrationsList,
    selectedIntegrationUuid: state => state.selectedIntegrationUuid,
    selectedIntegration: (state, getters) => getters.connectedIntegrations.find(i => i.uuid === getters.selectedIntegrationUuid) || {},
    selectedIntegrationSupportsFulfillment: (state, getters) => getters.selectedIntegration.supports_fulfillment,
    connectedIntegrations: (state, getters) => getters.teamIntegrationsList.filter(i => i.isConnected),
    getIntegrationModelByName: (state, getters) => name => getters.integrationsList.find(i => i.name === name) || {},

    getLocations: state => state.locations || [],
    getSubsidiaries: state => state.subsidiaries || [],
    getLocationByUuid: (state, getters) => uuid => getters.getLocations.find(l => l.uuid === uuid) || {},
    getCurrencyModelByCode: (state, getters) => code => getters.currencyList.find(el => el.code === code) || {}
}

const mutations = {
    [DATA_SET_ACCOUNT_STATS]: (state, stats) => {
        state.accountStats = stats || []
    },
    [DATA_SET_DOCUMENT_TAGS]: (state, tags) => {
        state.documentTags = tags || []
    },
    [DATA_SET_INTEGRATIONS]: (state, data) => {
        state.integrationsList = data || []
    },
    [DATA_SET_TEAM_INTEGRATIONS]: (state, data) => {
        state.teamIntegrationsList = data || []
    },
    [DATA_SET_TAX_RATES]: (state, data) => {
        state.taxRateList = data || []
    },
    [DATA_SET_TAX_TREATMENT]: (state, data) => {
        state.taxTreatmentList = data || []
    },
    [DATA_SET_UNIT_OF_MEASURE]: (state, data) => {
        state.unitOfMeasureList = data || []
    },
    [DATA_SET_RELATED_TEAMS]: (state, data) => {
        state.relatedTeams = data
    },
    [DATA_SET_LOCATIONS]: (state, data) => {
        state.locations = data
    },
    [DATA_SET_SUBSIDIARIES]: (state, data) => {
        state.subsidiaries = data
    },
    [DATA_SET_CURRENCIES]: (state, data) => {
        state.currencyList = data
    },
    [DATA_SET_INTEGRATION_UUID]: (state, uuid) => {
        state.selectedIntegrationUuid = uuid
    },
    [DATA_SET_PAYMENT_TERMS]: (state, data) => {
        state.paymentTermsList = data
    }
}

const actions = {
    [DATA_FETCH_DOCUMENT_TAGS]: async({commit}) => {
        const {data, error} = await tagsApi.fetchTags()
        if (error) {
            Raven.captureException(error)
        } else {
            commit(DATA_SET_DOCUMENT_TAGS, data)
        }
    },
    [DATA_SET_INTEGRATION_UUID]: ({commit, dispatch}, uuid) => {
        commit(DATA_SET_INTEGRATION_UUID, uuid)
        dispatch(DATA_FETCH_TEAM_DEPENDENT)
    },
    [DATA_FETCH_TEAM_DEPENDENT]: ({dispatch}) => {
        dispatch(DATA_FETCH_ENTITIES)
        dispatch(DATA_FETCH_UNIT_OF_MEASURE)
        dispatch(DATA_FETCH_TAX_RATES)
        dispatch(DATA_FETCH_TAX_TREATMENTS)
        dispatch(DATA_FETCH_INTEGRATIONS)
        dispatch(DATA_FETCH_TEAM_INTEGRATIONS)
        dispatch(DATA_FETCH_PAYMENT_TERMS)
        dispatch(DATA_FETCH_DOCUMENT_TAGS)
        dispatch(DATA_FETCH_ACCOUNT_STATS)
    },
    [DATA_FETCH_TAX_RATES]: async({commit, rootGetters}, query = '') => {
        const data = await fetchTaxOptions({params: {q: query, connection: rootGetters.selectedIntegrationUuid}})
        commit(DATA_SET_TAX_RATES, data)
    },
    [DATA_FETCH_TAX_TREATMENTS]: async({commit, rootGetters}, query = '') => {
        const data = await fetchTaxTreatments({params: {q: query, connection: rootGetters.selectedIntegrationUuid}})
        commit(DATA_SET_TAX_TREATMENT, data)
    },
    [DATA_FETCH_UNIT_OF_MEASURE]: async({commit, rootGetters}, query = '') => {
        const data = await fetchUnitOfMeasure({params: {q: query, connection: rootGetters.selectedIntegrationUuid}})
        commit(DATA_SET_UNIT_OF_MEASURE, data)
    },
    /**
     * @see Integration interface
     */
    [DATA_FETCH_INTEGRATIONS]: async({commit}) => {
        const res = await axios.get('connections')
        commit(DATA_SET_INTEGRATIONS, res.data)
    },
    /**
     * @see Connection interface
     */
    [DATA_FETCH_TEAM_INTEGRATIONS]: async({commit}) => {
        const res = await axios.get('connections/connections')
        commit(DATA_SET_TEAM_INTEGRATIONS, _.get(res, 'data.data', []))
    },
    [DATA_FETCH_PAYMENT_TERMS]: async({commit}) => {
        const data = await fetchPaymentTerms()
        commit(DATA_SET_PAYMENT_TERMS, data)
    },
    [DATA_FETCH_ENTITIES]: async({commit}) => {
        const res = await axios.get('teams/related')
        commit(DATA_SET_RELATED_TEAMS, _.get(res, 'data.data', []))
    },
    [DATA_FETCH_LOCATIONS]: async({commit}) => {
        const data = await fetchLocations()
        commit(DATA_SET_LOCATIONS, data)
    },
    [DATA_FETCH_SUBSIDIARIES]: async({commit}) => {
        const data = await fetchSubsidiaries()
        commit(DATA_SET_SUBSIDIARIES, data)
    },
    [DATA_FETCH_CURRENCIES]: async({commit}) => {
        const data = await fetchCurrencies()
        commit(DATA_SET_CURRENCIES, data)
    },
    [DATA_FETCH_ACCOUNT_STATS]: async({commit}) => {
        const stats = await fetchAccountStats()
        commit(DATA_SET_ACCOUNT_STATS, stats || {})
    }
}

export default {
    state,
    getters,
    actions,
    mutations
}
